import { useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router'
import { useAuth } from '../../lib/auth.js'
import { Button } from '../../ui/index.js'
import { LoadingState } from '../LoadingState.js'
import {
  notificationLinkFor,
  useMarkAllRead,
  useMarkRead,
  useNotificationList,
  useUnreadCount,
  type NotificationItem,
} from './useNotifications.js'

function BellIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3a6 6 0 0 0-6 6v3.3l-1.7 3.2a1 1 0 0 0 .9 1.5h13.6a1 1 0 0 0 .9-1.5L18 12.3V9a6 6 0 0 0-6-6Z"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinejoin="round"
      />
      <path d="M9.8 19a2.4 2.4 0 0 0 4.4 0" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" />
    </svg>
  )
}

export function NotificationBell() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [isOpen, setIsOpen] = useState(false)
  const [panelTop, setPanelTop] = useState(64)
  const containerRef = useRef<HTMLDivElement | null>(null)
  const unread = useUnreadCount()
  const list = useNotificationList(isOpen)
  const markRead = useMarkRead()
  const markAllRead = useMarkAllRead()

  useEffect(() => {
    if (!isOpen) return
    const onPointerDown = (event: PointerEvent) => {
      if (containerRef.current !== null && !containerRef.current.contains(event.target as Node)) {
        setIsOpen(false)
      }
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setIsOpen(false)
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [isOpen])

  if (user === null) return null

  const count = unread.data?.count ?? 0
  const items = list.data?.items ?? []

  const openItem = (item: NotificationItem) => {
    // Best-effort: the navigation must not wait on the write.
    if (item.readAt === null) markRead.mutate(item.id)
    setIsOpen(false)
    navigate(notificationLinkFor(user.role, item))
  }

  return (
    <div ref={containerRef} style={{ position: 'relative' }}>
      <Button
        kind="tertiary"
        type="button"
        aria-label={count > 0 ? `Notifications, ${count} unread` : 'Notifications'}
        aria-expanded={isOpen}
        onClick={() => {
          const rect = containerRef.current?.getBoundingClientRect()
          setPanelTop(rect !== undefined ? Math.round(rect.bottom + 8) : 64)
          setIsOpen((open) => !open)
        }}
        style={{
          position: 'relative',
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 32,
          height: 32,
          padding: 0,
          border: 'none',
          borderRadius: 'var(--r-md)',
          cursor: 'pointer',
          color: 'var(--fg-muted)',
        }}
      >
        <BellIcon />
        {count > 0 ? (
          <span
            aria-hidden="true"
            style={{
              position: 'absolute',
              top: 0,
              right: 0,
              minWidth: 15,
              height: 15,
              padding: '0 3px',
              borderRadius: 999,
              background: 'var(--negative)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 700,
              display: 'grid',
              placeItems: 'center',
              lineHeight: 1,
            }}
          >
            {count > 9 ? '9+' : count}
          </span>
        ) : null}
      </Button>

      {isOpen ? (
        <div
          role="region"
          aria-label="Notifications"
          style={{
            // Fixed to the VIEWPORT, not the bell: at 375px the bell sits
            // mid-topbar and a right-anchored absolute panel hangs 200px past
            // the left screen edge. Measured live before this was committed.
            position: 'fixed',
            right: 16,
            top: panelTop,
            width: 320,
            maxWidth: 'calc(100vw - 32px)',
            background: 'var(--surface)',
            border: '1px solid var(--border)',
            borderRadius: 'var(--r-lg)',
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.12)',
            padding: 'var(--o-space-2)',
            zIndex: 50,
          }}
        >
          <div className="row-between" style={{ padding: '4px 8px', alignItems: 'center' }}>
            <span className="card-label">Notifications</span>
            <Button kind="tertiary" size="small" onClick={() => markAllRead.mutate()} disabled={count === 0 || markAllRead.isPending}>
              Mark all read
            </Button>
          </div>

          {list.isPending ? <LoadingState label="Loading notifications" /> : null}
          {list.isError ? (
            <div style={{ padding: '10px 8px', fontSize: 13, color: 'var(--fg-muted)' }}>
              Could not load notifications.
            </div>
          ) : null}
          {list.isSuccess && items.length === 0 ? (
            <div style={{ padding: '10px 8px', fontSize: 13, color: 'var(--fg-muted)' }}>
              Nothing yet. Task activity will land here.
            </div>
          ) : null}

          {items.map((item) => (
            <Button
              kind="tertiary"
              key={item.id}
              type="button"
              onClick={() => openItem(item)}
              style={{
                width: '100%',
                display: 'flex',
                alignItems: 'flex-start',
                gap: 8,
                textAlign: 'left',
                padding: '10px 8px',
                border: 'none',
                borderRadius: 'var(--r-md)',
                background: item.readAt === null ? 'var(--brand-soft)' : 'transparent',
                cursor: 'pointer',
                color: 'var(--fg)',
                fontSize: 13,
                lineHeight: 1.4,
                // .btn sets white-space: nowrap, which is right for a button label and
                // wrong for a sentence. Without this a long title runs out of the panel
                // and is clipped rather than wrapping.
                whiteSpace: 'normal',
              }}
            >
              <span
                aria-hidden="true"
                style={{
                  flexShrink: 0,
                  width: 6,
                  height: 6,
                  marginTop: 6,
                  borderRadius: 999,
                  background: item.readAt === null ? 'var(--negative)' : 'transparent',
                }}
              />
              {/* minWidth:0 lets the flex child shrink; overflowWrap breaks a long
                  unspaced token (a reference like T-2029) instead of overflowing. */}
              <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}>{item.title}</span>
            </Button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
